import React, { createContext, useState, useContext, useEffect } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import axios from '../config/axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Load user from localStorage on init
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    // Check if we have a valid token
    if (token) {
      // Load user from localStorage first (for immediate display)
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setIsAuthenticated(true);
        } catch (e) {
          console.error('Error parsing saved user:', e);
        }
      }
      
      // Check if token is valid (regular login)
      try {
        // Try to get user info from a protected endpoint
        axios.get('/devices', {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(response => {
          // If we can get devices, token is valid
          setIsAuthenticated(true);
          // If we don't have user data, try to get it from token
          if (!user && savedUser) {
            const parsedUser = JSON.parse(savedUser);
            setUser(parsedUser);
          }
        })
        .catch(error => {
          // Token invalid, clear everything
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
        })
        .finally(() => setLoading(false));
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
      }
    } else {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
    }
  }, [token]);

  // Firebase sign in (Currently disabled - requires Firebase setup)
  const signInWithFirebase = async (email, password) => {
    return { 
      success: false, 
      error: "Firebase authentication is not configured. Please use Email/Password login instead." 
    };
  };

  // Firebase sign up (Currently disabled - requires Firebase setup)
  const signUpWithFirebase = async (email, password, name) => {
    return { 
      success: false, 
      error: "Firebase authentication is not configured. Please use Email/Password registration instead." 
    };
  };

  // Regular email sign in
  const signInWithEmail = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', {
        email,
        password
      });
      const { token: newToken, user } = response.data;
      setToken(newToken);
      setUser(user);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(user)); // Save user to localStorage
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  };

  // Regular email sign up
  const signUpWithEmail = async (email, password, name) => {
    try {
      console.log('Attempting registration with:', { email, name });
      const response = await axios.post('/auth/register', {
        email,
        password,
        name
      });
      console.log('Registration response:', response.data);
      const { token: newToken, user } = response.data;
      setToken(newToken);
      setUser(user);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(user)); // Save user to localStorage
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Registration failed';
      return { success: false, error: errorMessage };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      if (auth.currentUser) {
        await firebaseSignOut(auth);
      }
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      localStorage.removeItem('token');
      localStorage.removeItem('user'); // Clear user from localStorage
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const value = {
    user,
    isAuthenticated,
    token,
    loading,
    signInWithFirebase,
    signUpWithFirebase,
    signInWithEmail,
    signUpWithEmail,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};


